import { posthog } from '@/app-shell/flags/posthog-compat';
import { isTauri } from '@/tauri/utils';
import { FEATURE_FLAG_DOMAIN } from '@heidi/runtime/feature-flags';
import { OpenFeature, useBooleanFlagValue } from '@openfeature/react-sdk';
import { z } from 'zod';

/**
 * The regional servers' kill switch for honouring a team invite answer during
 * the onboarding gate's signup call. Read here as well: the V3 invite screen
 * on `/join-team` is the way into that flow, so it opens only while the
 * answer it collects will be acted on. The page is pre-login, so the
 * environment default is what is read, not a per-user override.
 */
const ONBOARD_TEAM_INVITE_FLAG_KEY = 'release-onboard-team-invite';

/**
 * Whether a signed-out person opening a team invite link sees the V3 invite
 * screen on `/join-team` (the hosted onboarding page's own screen, in its
 * layout) in place of the legacy account modal. Off restores the modal
 * without a deploy.
 */
export const useIsV3InviteLinkScreenEnabled = () => {
  const fromFlagsmith = useBooleanFlagValue(ONBOARD_TEAM_INVITE_FLAG_KEY, false);
  if (process.env.NEXT_PUBLIC_FORCE_ENABLE_ALL_FEATURE_FLAGS === 'true') return true;
  return fromFlagsmith;
};

/**
 * Gates the Kinde → Auth0 Apple migration flow: when enabled, eligible Kinde
 * users have the assign-apple-migration-ticket endpoint called and are shown
 * the forced migration modal. Off by default (opt-in rollout).
 *
 * Platform-split flags so the two ramps are fully decoupled:
 *   - Desktop (Tauri): `release-auth0-apple-migration-ticket-desktop`
 *   - Web: `release-auth0-apple-migration-ticket-web`
 *
 * Hook-order note: both hooks are called unconditionally, so hook order is
 * stable across renders regardless of ``isTauri()``.
 */
export const useIsAuth0AppleMigrationTicketEnabled = () => {
  const isWebEnabled = useBooleanFlagValue(
    'release-auth0-apple-migration-ticket-web',
    false
  );
  const isDesktopEnabled = useBooleanFlagValue(
    'release-auth0-apple-migration-ticket-desktop',
    false
  );
  return isTauri() ? isDesktopEnabled : isWebEnabled;
};

export const IS_CIBA_LOGIN_ENABLED = () => {
  return OpenFeature.getClient(FEATURE_FLAG_DOMAIN).getBooleanValue(
    'release-login-with-heidi-app',
    false
  );
};

export const IS_SSO_MICROSOFT_LOGOUT_ENABLED = () => {
  return posthog.isFeatureEnabled('release-sso-microsoft-logout');
};

/**
 * Hide Email Functionality: When enabled, hides all email-related features
 * in the application (e.g., email sharing, email notifications UI).
 */
export const IS_HIDE_EMAIL_FUNCTIONALITY_ENABLED = () => {
  return posthog.isFeatureEnabled('release-hide-email-functionality');
};

// Login link: control or removed
export const SIGN_UP_MODAL_LOGIN_LINK_VARIANTS = () => {
  const schema = z.enum(['control', 'removed']).catch('control');
  return schema.parse(
    posthog.getFeatureFlag('release-sign-up-modal-login-link-variants')
  );
};

export const IS_DESKTOP_AUTH0_AUTH_ENABLED = () => {
  if (!isTauri()) {
    return false;
  }
  return posthog.isFeatureEnabled('desktop-auth0-auth');
};

/**
 * Desktop Logout-on-Exit: When enabled, the desktop app will automatically
 * logout the user when the app is closed. This is intended for enterprise
 * users on shared devices where automatic session persistence is a security concern.
 *
 * Only applies when using Auth0 Desktop authentication.
 */
export const IS_DESKTOP_LOGOUT_ON_EXIT_ENABLED = () => {
  if (!isTauri()) {
    return false;
  }
  return posthog.isFeatureEnabled('desktop-auth0-logout-on-exit');
};

export const IS_SEND_GET_MOBILE_EMAIL_ENABLED = () => {
  return posthog.isFeatureEnabled('release-send-get-mobile-email');
};

export const IS_AUTH0_SOCIAL_LOGIN_APPLE_ENABLED = () => {
  if (process.env.NEXT_PUBLIC_FORCE_KINDE === 'true') return false;
  return posthog.isFeatureEnabled('release-auth-auth0-social-login-apple');
};

export const IS_CHANGE_EMAIL_ENABLED = () => {
  return posthog.isFeatureEnabled('release-change-email');
};
